import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { initialStatus, LoadingStatus } from '@bcef/ia-shared';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { debounceTime, forkJoin, pipe, switchMap, tap } from 'rxjs';

import { CofagSimulationRequest } from '@features/cofag/models/cofag-simulation-request';
import { CofagSimulationResponse } from '@features/cofag/models/cofag-simulation-response.model';
import { CofagSimulationService } from '@features/cofag/services/cofag-simulation.service';
import { ErrorStore } from '@shared/store/error.store';

import { CfagSimulationRequest } from '../models/cfag-simulation-request';
import { CfagSimulationResponse } from '../models/cfag-simulation-response.model';
import { CfagSimulationService } from '../services/cfag-simulation.service';

export interface CfagState {
  cfagSimulationRequest: Partial<CfagSimulationRequest>;
  cofagSimulationRequest: Partial<CofagSimulationRequest>;
  cfagSimulationResponse: Partial<CfagSimulationResponse>;
  cofagSimulationResponse: Partial<CofagSimulationResponse>;
  loadingStatus: LoadingStatus;
}
export const initialState: CfagState = {
  cfagSimulationRequest: {},
  cofagSimulationRequest: {},
  cfagSimulationResponse: {},
  cofagSimulationResponse: {},
  loadingStatus: initialStatus,
};

export const SEND_CFAG_FORM_DEBOUNCE_MILLIS = 300;

export const CfagStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withStorageSync({ key: 'ia-simulateur-epargne:cfag', storage: () => sessionStorage }),
  withMethods(
    (
      store,
      cfagSimulationService = inject(CfagSimulationService),
      cofagSimulationService = inject(CofagSimulationService),
      errorStore = inject(ErrorStore),
      router = inject(Router),
    ) => ({
      sendCfagForm: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              loadingStatus: {
                ...state.loadingStatus,
                isLoading: true,
              },
            })),
          ),
          debounceTime(SEND_CFAG_FORM_DEBOUNCE_MILLIS),
          switchMap(() =>
            forkJoin({
              cfagSimulationResponse: cfagSimulationService.postCfagSimulation(
                store.cfagSimulationRequest(),
              ),
              cofagSimulationResponse: cofagSimulationService.postCofagSimulation(
                store.cfagSimulationRequest(),
              ),
            }).pipe(
              tapResponse({
                next: ({ cfagSimulationResponse, cofagSimulationResponse }) => {
                  patchState(store, (state) => ({
                    cfagSimulationResponse: cfagSimulationResponse,
                    cofagSimulationResponse: cofagSimulationResponse,
                    loadingStatus: { ...state.loadingStatus, isLoaded: true },
                  }));
                  router.navigate(['cfag', 'summary']);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setError(error);
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, error },
                  }));
                  router.navigate(['erreur']);
                },
                finalize: () =>
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, isLoading: false },
                  })),
              }),
            ),
          ),
        ),
      ),
      addResponses(cfagSimulationRequest: Partial<CfagSimulationRequest>): void {
        patchState(store, { cfagSimulationRequest });
      },
    }),
  ),
);
export type ICfagStore = InstanceType<typeof CfagStore>;
